import {
  Component,
  OnInit,
  inject,
  CUSTOM_ELEMENTS_SCHEMA,
  ChangeDetectionStrategy,
  ChangeDetectorRef
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { DateTime } from 'luxon';

import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { WindowService } from 'src/shared/providers/window.service';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { Util } from 'src/shared/util/util';
import { WebsiteConfig } from 'src/shared/services/direct-booking-sites.service';
import { InquiriesService, CreateProposalParams } from 'src/shared/services/inquiries.service';
import { ListingOptimizationSettingsService } from 'src/shared/services/listing-optimization-settings.service';
import { parseInquiryActionError } from 'src/models/inquiry.model';
import { ListingCalendarService, ListingCalendarDay } from 'src/shared/services/listing-calendar.service';
import { environment } from 'src/environments/environment';
import { DemoBlockService } from 'src/shared/services/demo-block.service';

import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';
import { SharedModule } from 'src/shared/shared.module';

@Component({
  selector: 'website-booking-link',
  templateUrl: './website-booking-link.component.html',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TidyUiComponentsModule, SharedModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class WebsiteBookingLinkComponent implements OnInit {
  private readonly navCtrl = inject(CustomNavController);
  private readonly rightSidePanelService = inject(RightSidePanelService);
  private readonly storage = inject(TidyStorage);
  private readonly util = inject(Util);
  private readonly demoBlock = inject(DemoBlockService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly fb = inject(FormBuilder);
  private readonly inquiriesService = inject(InquiriesService);
  private readonly listingCalendar = inject(ListingCalendarService);
  private readonly listingSettings = inject(ListingOptimizationSettingsService);

  readonly windowService = inject(WindowService);

  config: WebsiteConfig | null = null;
  isRightSideContent = false;

  form = this.fb.group({
    price: ['', [Validators.required, Validators.min(1)]],
    securityDeposit: ['', [Validators.min(0)]],
    guestName: [''],
    adults: ['', [Validators.min(1)]],
    children: ['', [Validators.min(0)]],
    note: [''],
    expiresAt: [null as Date | null],
  });
  dateRangeForm = this.fb.group({ start: '', end: '' });
  calendar: ListingCalendarDay[] = [];
  isLoading = true;
  checkInDate: string | null = null;
  checkOutDate: string | null = null;
  standardPriceCents: number | null = null;
  dateError = '';
  link: string | null = null;
  submitted = false;
  isSaving = false;

  async ngOnInit() {
    this.isRightSideContent = await this.storage.retrieve('dialog-right-side-open') || false;
    this.config = this.navCtrl.getParam('websiteConfig', false);
    if (!this.config) {
      const dialogParams = await this.storage.retrieve('dialog-params');
      this.config = dialogParams?.websiteConfig;
    }
    this.rightSidePanelService.setDialogPageTitle('Create Booking Link');
    this.rightSidePanelService.setDialogPageSubtitle(
      this.config?.property_address || this.config?.property_name || ''
    );
    if (this.config) {
      const today = DateTime.local().toISODate() ?? '';
      const oneYearOut = DateTime.local().plus({ days: 365 }).toISODate() ?? '';
      try {
        this.calendar = await this.listingCalendar.getMonth(this.config.id, today, oneYearOut);
      } catch {
        this.calendar = [];
      }
      const depositCents = await this.listingSettings.getListingSecurityDeposit(this.config.id).catch(() => null);
      if (depositCents) { this.form.patchValue({ securityDeposit: String(depositCents / 100) }); }
    }
    this.isLoading = false;
    this.cdr.markForCheck();
  }

  // Booking the link charges the guest immediately, so a price far below the
  // standard one (a typo, or a nightly rate typed as the stay total) is flagged.
  get priceWarning(): string {
    const priceCents = Math.round(Number(this.form.controls.price.value) * 100);
    if (!this.standardPriceCents || !priceCents || priceCents >= this.standardPriceCents * 0.5) { return ''; }
    const percentBelow = Math.floor((1 - priceCents / this.standardPriceCents) * 100);
    return `This is ${percentBelow}% below the standard price. The guest is charged this amount as soon as they book.`;
  }

  // A day stays pickable while it can still start a stay (its night is free) or
  // end one (the night before it is free); only days inside a booked or blocked
  // run are greyed out. Ranges that span a taken night are still rejected below.
  dateFilter = (date: unknown): boolean => {
    const iso = this.toIsoDate(date);
    if (!iso) { return true; }
    if (iso < (DateTime.local().toISODate() ?? '')) { return false; }
    const previous = DateTime.fromISO(iso).minus({ days: 1 }).toISODate() ?? '';
    return !this.isTakenNight(iso) || !this.isTakenNight(previous);
  };

  private isTakenNight(iso: string): boolean {
    const day = this.calendar.find(entry => entry.date === iso);
    return !!day && (day.isBooked || day.isBlocked);
  }

  // tidy-daterange's customDateOption emits its form group's plain .value, not the
  // group itself.
  onDateRange(value: { start?: unknown; end?: unknown } | null) {
    const start = this.toIsoDate(value?.start);
    const end = this.toIsoDate(value?.end);
    const hasRange = !!start && !!end && end > start;

    this.checkInDate = hasRange ? start : null;
    this.checkOutDate = hasRange ? end : null;

    if (!hasRange) {
      this.dateError = '';
      this.standardPriceCents = null;
      return;
    }

    const today = DateTime.local().toISODate() ?? '';
    if (start < today) {
      this.dateError = 'Check-in must be today or later.';
      this.checkInDate = null;
      this.checkOutDate = null;
      this.standardPriceCents = null;
      return;
    }

    const nights = this.calendar.filter(day => day.date >= start && day.date < end);
    this.dateError = nights.some(night => night.isBooked || night.isBlocked)
      ? 'Those dates include a booked or blocked night.'
      : '';
    this.standardPriceCents = nights.length > 0 && nights.every(night => night.price !== null)
      ? nights.reduce((sum, night) => sum + (night.price ?? 0), 0)
      : null;
  }

  save = async () => {
    if (this.demoBlock.block()) {
      return;
    }
    this.submitted = true;
    if (!this.checkInDate || !this.checkOutDate) {
      this.dateError = 'Please select the stay dates.';
      this.cdr.markForCheck();
      return;
    }
    if (!this.form.valid || this.dateError || !this.config) {
      return;
    }

    this.isSaving = true;
    this.cdr.markForCheck();

    const raw = this.form.value;
    const params: CreateProposalParams = {
      address_id: this.config.id,
      check_in_date: this.checkInDate,
      check_out_date: this.checkOutDate,
      nights_total_amount: Math.round(Number(raw.price) * 100),
      security_deposit_amount: raw.securityDeposit ? Math.round(Number(raw.securityDeposit) * 100) : 0,
    };
    if (raw.guestName) { params.guest_name = raw.guestName; }
    if (raw.adults || raw.children) {
      params.party = {
        ...(raw.adults ? { total_adults: Number(raw.adults) } : {}),
        ...(raw.children ? { total_children: Number(raw.children) } : {}),
      };
    }
    if (raw.note) { params.note = raw.note; }
    if (raw.expiresAt) {
      params.expires_at = DateTime.fromISO(this.toIsoDate(raw.expiresAt)).endOf('day').toISO() ?? undefined;
    }

    try {
      const inquiry = await firstValueFrom(this.inquiriesService.createProposal(params));
      this.link = this.linkFor(inquiry.uuid);
      this.util.showSuccess('Booking link created');
    } catch (err: unknown) {
      const parsed = parseInquiryActionError(err);
      const message = parsed.message
        || (parsed.code === 422 ? 'Those dates are no longer available.' : 'Could not create the booking link. Please try again.');
      this.util.showError(message);
    } finally {
      this.isSaving = false;
      this.cdr.markForCheck();
    }
  };

  copyLink() {
    this.util.copyToClipBoard(this.link);
  }

  done = () => {
    this.goBack();
  };

  goBack() {
    if (this.isRightSideContent) {
      this.rightSidePanelService.goBack();
    } else {
      this.navCtrl.back();
    }
  }

  private linkFor(uuid: string): string {
    const base = this.config?.custom_domain ? `https://${this.config.custom_domain}` : environment.pmsites_url;
    return `${base}/p/${uuid}`;
  }

  // Normalizes a picker value (Date, Luxon DateTime or ISO-ish string) to YYYY-MM-DD.
  private toIsoDate(value: unknown): string {
    if (!value) { return ''; }
    if (value instanceof Date) {
      return DateTime.fromJSDate(value).toISODate() ?? '';
    }
    if (DateTime.isDateTime(value)) {
      return value.toISODate() ?? '';
    }
    if (typeof value === 'string') {
      const parsed = DateTime.fromISO(value);
      return parsed.isValid ? (parsed.toISODate() ?? '') : value.slice(0, 10);
    }
    return '';
  }
}
